import React, { useEffect, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  CircularProgress,
  Divider,
  Dialog,
  DialogContent,
  DialogTitle,
  Grid,
  IconButton,
  Stack,
  Typography,
} from '@mui/material';
import { alpha } from '@mui/material/styles';
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, Cell } from 'recharts';
import {
  CalendarToday as CalendarIcon,
  CheckCircle as CheckCircleIcon,
  ContentCopy as ContentCopyIcon,
  Description as DescriptionIcon,
  Download as DownloadIcon,
  EventNote as EventNoteIcon,
  FitnessCenter as FitnessCenterIcon,
  Notifications as NotificationsIcon,
  PlayArrow as PlayIcon,
  SmartToy as SmartToyIcon,
  Timeline as TrendingUpIcon,
  AccessTime as TimeIcon,
} from '@mui/icons-material';
import {
  FocusedProgressItem,
  FocusedProgressPeriodSummary,
  WorkoutTrainerReport,
  SessionLogData,
  SessionLogSet,
  SessionPlannedLogSet,
  TrainingProgram,
  WorkoutSession,
} from '../services/types';
import { TrainingProgramService } from '../services/trainingProgramService';
import { WorkoutSessionService } from '../services/workoutSessionService';
import { ProgramExerciseService } from '../services/programExerciseService';
import { ExportService } from '../services/exportService';
import ImagePlaceholder from './ImagePlaceholder';

const PRE_WORKOUT_FALLBACK = 'няма наличен check-in';
const PLANNED_EXERCISES_FALLBACK = 'няма планирани упражнения';
const EXECUTED_EXERCISES_FALLBACK = 'няма изпълнение';
const WORKOUT_SESSION_UPDATED_EVENT = 'workout-session-updated';
const PROGRAM_EXERCISES_UPDATED_EVENT = 'program-exercises-updated';
const FOCUSED_PROGRESS_PERIODS: Array<{ key: keyof FocusedProgressItem['summary']; label: string }> = [
  { key: '30d', label: '1 месец' },
  { key: '90d', label: '3 месеца' },
  { key: '180d', label: '6 месеца' },
];

const formatMetricValue = (value: number | null | undefined): string => {
  if (value === null || value === undefined || Number.isNaN(value)) {
    return '-';
  }

  return Number.isInteger(value) ? String(value) : value.toFixed(2).replace(/\.?0+$/, '');
};

const formatPlannedSetLine = (set: SessionPlannedLogSet): string => {
  const details: string[] = [];

  details.push(`${set.targetReps ?? '-'} повт.`);

  if (set.targetWeight !== null && set.targetWeight !== undefined) {
    details.push(`${formatMetricValue(set.targetWeight)} кг`);
  }

  if (set.targetRpe !== null && set.targetRpe !== undefined) {
    details.push(`RPE ${set.targetRpe}`);
  }

  const prefix = set.setOrder !== null && set.setOrder !== undefined ? `${set.setOrder}. ` : '- ';
  return `${prefix}${details.join(' | ')}`;
};

const formatExecutedSetLine = (set: SessionLogSet, index: number): string => {
  const details = [`${set.reps} повт.`, `${formatMetricValue(set.weight)} кг`];

  if (set.rpe !== null && set.rpe !== undefined) {
    details.push(`RPE ${set.rpe}`);
  }

  if (set.assistance) {
    details.push('с помощ');
  }

  return `${index + 1}. ${details.join(' | ')}`;
};

const formatWorkoutLog = (data: SessionLogData): string => {
  const [year, month, day] = data.date.split('-');
  const formattedDate = `${day}.${month}.${year}`;
  const lines: string[] = [];

  lines.push(`Тренировка: ${data.name} (${formattedDate})`);
  if (data.gymName) {
    lines.push(`Зала: ${data.gymName}`);
  }
  if (data.durationMinutes) {
    const hours = Math.floor(data.durationMinutes / 60);
    const mins = data.durationMinutes % 60;
    const durationStr = hours > 0 ? `${hours}ч ${mins}мин` : `${mins}мин`;
    lines.push(`Продължителност: ${durationStr}`);
  }
  if (data.notes) {
    lines.push(`Бележки: ${data.notes}`);
  }
  lines.push(`Общо състояние преди тренировката: ${data.preWorkoutSummary ?? PRE_WORKOUT_FALLBACK}`);
  if ((data.focusedExerciseNames ?? []).length > 0) {
    lines.push(`Следим прогрес на: ${data.focusedExerciseNames!.join(', ')}`);
  }
  lines.push('');
  lines.push('планирано:');

  if (data.plannedExercises.length === 0) {
    lines.push(`- ${PLANNED_EXERCISES_FALLBACK}`);
  } else {
    for (const exercise of data.plannedExercises) {
      lines.push(`- ${exercise.exerciseName}`);

      if (exercise.sets.length === 0) {
        lines.push('  няма планирани серии');
        continue;
      }

      for (const set of exercise.sets) {
        lines.push(`  ${formatPlannedSetLine(set)}`);

        if (set.notes) {
          lines.push(`    бележка: ${set.notes}`);
        }
      }
    }
  }

  lines.push('');
  lines.push('изпълнено:');

  if (data.exercises.length === 0) {
    lines.push(`- ${EXECUTED_EXERCISES_FALLBACK}`);
  } else {
    for (const exercise of data.exercises) {
      lines.push(`- ${exercise.exerciseName}`);

      if (exercise.sets.length === 0) {
        lines.push('  няма изпълнени серии');
        continue;
      }

      exercise.sets.forEach((set, index) => {
        lines.push(`  ${formatExecutedSetLine(set, index)}`);

        if (set.notes) {
          lines.push(`    бележка: ${set.notes}`);
        }
      });
    }
  }

  return lines.join('\n');
};

const formatSignedMetric = (value: number | null, unit: string): string => {
  if (value === null || Number.isNaN(value)) {
    return '-';
  }

  const formatted = formatMetricValue(Math.abs(value));
  const sign = value > 0 ? '+' : value < 0 ? '-' : '+';
  return `${sign}${formatted} ${unit}`;
};

function FocusedProgressWidget({
  items,
  loading,
  error,
  onViewPrograms,
}: {
  items: FocusedProgressItem[];
  loading: boolean;
  error: string | null;
  onViewPrograms: () => void;
}) {
  return (
    <Card elevation={0} sx={{ backgroundColor: alpha('#08111f', 0.26), height: '100%' }}>
      <CardContent sx={{ p: 2.25, '&:last-child': { pb: 2.25 } }}>
        <Stack spacing={1.5}>
          <Box>
            <Typography variant="subtitle2" gutterBottom>
              Фокусен прогрес
            </Typography>
            <Typography variant="body2" color="text.secondary">
              Кратка справка за упражненията, които следиш най-отблизо.
            </Typography>
          </Box>

          {loading ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', py: 3 }}>
              <CircularProgress size={24} />
            </Box>
          ) : null}

          {!loading && error ? (
            <Alert severity="warning">{error}</Alert>
          ) : null}

          {!loading && !error && items.length === 0 ? (
            <Alert severity="info" action={<Button color="inherit" size="small" onClick={onViewPrograms}>Маркирай</Button>}>
              Нямаш маркирани focus упражнения. Отвори програмата и отбележи движенията, които искаш да следиш тук.
            </Alert>
          ) : null}

          {!loading && !error && items.length > 0 ? (
            <Stack spacing={1.5}>
              {items.map((item) => (
                <Card key={item.programExerciseId} variant="outlined" sx={{ borderRadius: 3, bgcolor: alpha('#020617', 0.16) }}>
                  <CardContent sx={{ p: 1.5, '&:last-child': { pb: 1.5 } }}>
                    <Stack spacing={1.25}>
                      <Box>
                        <Typography variant="subtitle1">{item.exerciseName}</Typography>
                        <Typography variant="caption" color="text.secondary">
                          {item.programName} • Ден {item.dayOfWeek} • #{item.orderInSession}
                        </Typography>
                      </Box>

                      <Grid container spacing={1}>
                        {FOCUSED_PROGRESS_PERIODS.map((period) => {
                          const periodSummary = item.summary[period.key];

                          return (
                            <Grid key={period.key} size={{ xs: 12, sm: 4 }}>
                              <Card variant="outlined" sx={{ height: '100%', borderRadius: 2 }}>
                                <CardContent sx={{ p: 1.25, '&:last-child': { pb: 1.25 } }}>
                                  <Stack spacing={0.75}>
                                    <Typography variant="caption" color="text.secondary">
                                      {period.label}
                                    </Typography>

                                    {periodSummary.insufficientData ? (
                                      <Typography variant="body2">Недостатъчно данни</Typography>
                                    ) : (
                                      <>
                                        <Typography variant="body2">{formatSignedMetric(periodSummary.deltaAvgWeightKg, 'kg')}</Typography>
                                        <Typography variant="body2">{formatSignedMetric(periodSummary.deltaAvgRepsPerSet, 'повт.')}</Typography>
                                        <Typography variant="body2">{formatSignedMetric(periodSummary.deltaAvgRpe, 'RPE')}</Typography>
                                      </>
                                    )}

                                    <Typography variant="caption" color="text.secondary">
                                      {formatFocusedProgressSessions(periodSummary)}
                                    </Typography>
                                  </Stack>
                                </CardContent>
                              </Card>
                            </Grid>
                          );
                        })}
                      </Grid>
                    </Stack>
                  </CardContent>
                </Card>
              ))}
            </Stack>
          ) : null}
        </Stack>
      </CardContent>
    </Card>
  );
}

const formatFocusedProgressSessions = (periodSummary: FocusedProgressPeriodSummary): string =>
  `${periodSummary.currentWindowSessionCount}/${periodSummary.previousWindowSessionCount} сесии`;

const MUSCLE_GROUP_LABELS: Record<string, string> = {
  chest: 'Гърди',
  back: 'Гръб',
  quadriceps: 'Квадрицепс',
  hamstrings: 'Задно бедро',
  adductors: 'Аддуктори',
  glutes: 'Глутеуси',
  calves: 'Икри',
  core: 'Корем',
  arms: 'Ръце',
  shoulders: 'Рамене',
};

const MUSCLE_GROUP_COLORS: Record<string, string> = {
  chest: '#ef5350',
  back: '#42a5f5',
  quadriceps: '#66bb6a',
  hamstrings: '#43a047',
  adductors: '#9ccc65',
  glutes: '#ec407a',
  calves: '#8d6e63',
  core: '#ffa726',
  arms: '#ab47bc',
  shoulders: '#26c6da',
};

const MUSCLE_GROUP_ORDER = ['chest', 'back', 'quadriceps', 'hamstrings', 'adductors', 'glutes', 'calves', 'shoulders', 'arms', 'core'];

function WeeklyMuscleChart({ data }: { data: Record<string, number> }) {
  const chartData = MUSCLE_GROUP_ORDER.map((key) => ({
    name: MUSCLE_GROUP_LABELS[key] || key,
    sets: data[key] || 0,
    key,
  }));

  const hasData = chartData.some((d) => d.sets > 0);

  return (
    <Card elevation={0}>
      <CardContent sx={{ p: { xs: 2.25, md: 3 }, '&:last-child': { pb: { xs: 2.25, md: 3 } } }}>
        <Typography variant="h5" gutterBottom>
          Седмично натоварване
        </Typography>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
          Серии по мускулна група тази седмица
        </Typography>
        <Divider sx={{ mb: 2 }} />
        {!hasData ? (
          <Typography variant="body2" color="text.secondary">
            Няма логнати тренировки тази седмица
          </Typography>
        ) : (
          <Box sx={{ width: '100%', height: 220 }}>
            <ResponsiveContainer width="100%" height="100%">
              <BarChart data={chartData} margin={{ top: 5, right: 5, left: -10, bottom: 5 }}>
                <CartesianGrid strokeDasharray="3 3" stroke="rgba(255,255,255,0.1)" />
                <XAxis dataKey="name" tick={{ fontSize: 12 }} />
                <YAxis allowDecimals={false} tick={{ fontSize: 12 }} />
                <Tooltip
                  contentStyle={{ backgroundColor: '#1e293b', border: '1px solid #334155', borderRadius: 8 }}
                  labelStyle={{ color: '#e2e8f0' }}
                  formatter={(value: number | string) => [`${value} серии`, 'Серии']}
                />
                <Bar dataKey="sets" radius={[4, 4, 0, 0]}>
                  {chartData.map((entry) => (
                    <Cell key={entry.key} fill={MUSCLE_GROUP_COLORS[entry.key] || '#8884d8'} />
                  ))}
                </Bar>
              </BarChart>
            </ResponsiveContainer>
          </Box>
        )}
      </CardContent>
    </Card>
  );
}

interface DashboardProps {
  onStartWorkout: (programId: number, dayOfWeek: number) => void;
  onViewPrograms: () => void;
  onPlanWorkout?: () => void;
  onOpenNotifications?: () => void;
}

export default function Dashboard({
  onStartWorkout,
  onViewPrograms,
  onPlanWorkout,
  onOpenNotifications,
}: DashboardProps) {
  const [activePrograms, setActivePrograms] = useState<TrainingProgram[]>([]);
  const [recentWorkouts, setRecentWorkouts] = useState<WorkoutSession[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [programDays, setProgramDays] = useState<{ [programId: number]: number[] }>({});
  const [exporting, setExporting] = useState(false);
  const [copiedWorkoutId, setCopiedWorkoutId] = useState<number | null>(null);
  const [copyingWorkoutId, setCopyingWorkoutId] = useState<number | null>(null);
  const [trainerReportsByWorkoutId, setTrainerReportsByWorkoutId] = useState<Record<number, WorkoutTrainerReport>>({});
  const [reportDialogWorkoutId, setReportDialogWorkoutId] = useState<number | null>(null);
  const [reportDialogWorkoutLog, setReportDialogWorkoutLog] = useState<string>('');
  const [reportDialogLogLoading, setReportDialogLogLoading] = useState(false);
  const [weeklyMuscleSets, setWeeklyMuscleSets] = useState<Record<string, number>>({});
  const [focusedProgressItems, setFocusedProgressItems] = useState<FocusedProgressItem[]>([]);
  const [focusedProgressLoading, setFocusedProgressLoading] = useState(true);
  const [focusedProgressError, setFocusedProgressError] = useState<string | null>(null);

  useEffect(() => {
    void loadDashboardData();
    void loadFocusedProgressData();
  }, []);

  useEffect(() => {
    const handleDashboardRefresh = () => {
      void loadDashboardData();
      void loadFocusedProgressData();
    };

    window.addEventListener(WORKOUT_SESSION_UPDATED_EVENT, handleDashboardRefresh);
    window.addEventListener(PROGRAM_EXERCISES_UPDATED_EVENT, handleDashboardRefresh);

    return () => {
      window.removeEventListener(WORKOUT_SESSION_UPDATED_EVENT, handleDashboardRefresh);
      window.removeEventListener(PROGRAM_EXERCISES_UPDATED_EVENT, handleDashboardRefresh);
    };
  }, []);

  const loadDashboardData = async () => {
    try {
      setLoading(true);
      setError(null);

      const programs = await TrainingProgramService.getAll();
      const active = programs.filter((program) => program.active);
      setActivePrograms(active);

      const daysMap: { [programId: number]: number[] } = {};
      for (const program of active) {
        const response = await ProgramExerciseService.getProgramExercises(program.id);
        daysMap[program.id] = Object.keys(response.exercisesByDay)
          .map(Number)
          .sort((a, b) => a - b);
      }
      setProgramDays(daysMap);

      const endDate = new Date().toISOString().split('T')[0];
      const startDate = new Date(Date.now() - 7 * 24 * 60 * 60 * 1000).toISOString().split('T')[0];
      const recent = await WorkoutSessionService.getByDateRange(startDate, endDate);
      const withSets = recent.filter((workout) => workout.sets && workout.sets.length > 0);
      const sortedRecentWorkouts = withSets
        .sort((a, b) => new Date(b.workoutDate).getTime() - new Date(a.workoutDate).getTime())
        .slice(0, 5);
      setRecentWorkouts(sortedRecentWorkouts);

      const reports = await WorkoutSessionService.getTrainerReports(sortedRecentWorkouts.map((workout) => workout.id));
      setTrainerReportsByWorkoutId(reports);

      // Fetch weekly muscle sets for active program
      const activeProgramId = active.length > 0 ? active[0].id : undefined;
      const monday = new Date();
      monday.setDate(monday.getDate() - ((monday.getDay() + 6) % 7));
      const sunday = new Date(monday);
      sunday.setDate(sunday.getDate() + 6);
      const muscleData = await WorkoutSessionService.getWeeklyMuscleSets(
        monday.toISOString().split('T')[0],
        sunday.toISOString().split('T')[0],
        activeProgramId,
      );
      setWeeklyMuscleSets(muscleData.sets);
    } catch (err) {
      setError('Failed to load dashboard data');
      console.error(err);
      setTrainerReportsByWorkoutId({});
    } finally {
      setLoading(false);
    }
  };

  const loadFocusedProgressData = async () => {
    try {
      setFocusedProgressLoading(true);
      setFocusedProgressError(null);
      const response = await WorkoutSessionService.getFocusedProgressSummary();
      setFocusedProgressItems(response.items);
    } catch (err) {
      console.error(err);
      setFocusedProgressItems([]);
      setFocusedProgressError('Неуспешно зареждане на focus прогреса.');
    } finally {
      setFocusedProgressLoading(false);
    }
  };

  const handleQuickStart = (programId: number) => {
    const days = programDays[programId] || [];
    if (days.length > 0) {
      onStartWorkout(programId, days[0]);
    }
  };

  const formatDate = (dateString: string) => {
    const date = new Date(dateString);
    const today = new Date();
    const yesterday = new Date(today);
    yesterday.setDate(yesterday.getDate() - 1);

    if (date.toDateString() === today.toDateString()) {
      return 'Днес';
    }
    if (date.toDateString() === yesterday.toDateString()) {
      return 'Вчера';
    }

    return date.toLocaleDateString('bg-BG', {
      weekday: 'short',
      month: 'short',
      day: 'numeric',
    });
  };

  const getStatusColor = (status: WorkoutSession['status']) => {
    switch (status) {
      case 'completed':
        return 'success';
      case 'in_progress':
        return 'warning';
      case 'skipped':
        return 'error';
      default:
        return 'default';
    }
  };

  const getStatusLabel = (status: WorkoutSession['status']) => {
    switch (status) {
      case 'completed':
        return 'Завършена';
      case 'in_progress':
        return 'В процес';
      case 'skipped':
        return 'Пропусната';
      default:
        return 'Планирана';
    }
  };

  const handleCompleteWorkout = async (workoutId: number) => {
    try {
      const completedWorkout = await WorkoutSessionService.markAsCompleted(workoutId);
      setRecentWorkouts((prev) =>
        prev.map((workout) => (workout.id === workoutId ? completedWorkout : workout)),
      );
      window.dispatchEvent(new CustomEvent(WORKOUT_SESSION_UPDATED_EVENT));
    } catch (err) {
      setError('Грешка при завършване на тренировката');
      console.error(err);
    }
  };

  const handleExportCsv = async () => {
    try {
      setExporting(true);
      setError(null);
      await ExportService.downloadCsv();
    } catch (err) {
      setError('Грешка при експортиране на CSV');
      console.error(err);
    } finally {
      setExporting(false);
    }
  };

  const handleCopyWorkoutLog = async (workoutId: number) => {
    try {
      setCopyingWorkoutId(workoutId);
      const logData = await WorkoutSessionService.getLogData(workoutId);
      const formattedLog = formatWorkoutLog(logData);

      if (navigator.clipboard && navigator.clipboard.writeText) {
        await navigator.clipboard.writeText(formattedLog);
      } else {
        const textArea = document.createElement('textarea');
        textArea.value = formattedLog;
        textArea.style.position = 'fixed';
        textArea.style.left = '-9999px';
        document.body.appendChild(textArea);
        textArea.select();
        document.execCommand('copy');
        document.body.removeChild(textArea);
      }

      setCopiedWorkoutId(workoutId);
      window.setTimeout(() => setCopiedWorkoutId(null), 2000);
    } catch (err) {
      setError('Грешка при копиране на тренировката');
      console.error(err);
    } finally {
      setCopyingWorkoutId(null);
    }
  };

  const handleOpenReportDialog = async (workoutId: number) => {
    setReportDialogWorkoutId(workoutId);
    setReportDialogWorkoutLog('');
    setReportDialogLogLoading(true);
    try {
      const logData = await WorkoutSessionService.getLogData(workoutId);
      setReportDialogWorkoutLog(formatWorkoutLog(logData));
    } catch (err) {
      console.error(err);
      setReportDialogWorkoutLog('Неуспешно зареждане на лог за тренировката.');
    } finally {
      setReportDialogLogLoading(false);
    }
  };

  const handleCloseReportDialog = () => {
    setReportDialogWorkoutId(null);
    setReportDialogWorkoutLog('');
    setReportDialogLogLoading(false);
  };

  const totalCompletedWorkouts = recentWorkouts.filter((workout) => workout.status === 'completed' && workout.sets && workout.sets.length > 0).length;
  const totalTrackedMinutes = recentWorkouts.reduce((sum, workout) => sum + (workout.durationMinutes || 0), 0);
  const recommendedProgram = activePrograms[0] || null;
  const selectedWorkout = reportDialogWorkoutId !== null
    ? recentWorkouts.find((workout) => workout.id === reportDialogWorkoutId) ?? null
    : null;
  const selectedWorkoutReport = reportDialogWorkoutId !== null
    ? trainerReportsByWorkoutId[reportDialogWorkoutId] ?? null
    : null;

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '60vh' }}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Stack spacing={{ xs: 2, md: 3 }} data-testid="dashboard-layout">
      <Card
        elevation={0}
        sx={{
          overflow: 'hidden',
          background: `linear-gradient(145deg, ${alpha('#7dd3fc', 0.16)} 0%, ${alpha('#8bffb0', 0.07)} 100%)`,
        }}
      >
        <CardContent sx={{ p: { xs: 2.5, md: 3.5 }, '&:last-child': { pb: { xs: 2.5, md: 3.5 } } }}>
          <Grid container spacing={{ xs: 2.5, md: 3 }}>
            <Grid size={{ xs: 12, md: 7 }}>
              <Stack spacing={2.5}>
                <Box>
                  <Typography variant="overline" sx={{ color: 'primary.light' }}>
                    Добре дошъл
                  </Typography>
                  <Typography variant="h3" component="h1" gutterBottom>
                    Начало
                  </Typography>
                  <Typography variant="h6" color="text.secondary" sx={{ maxWidth: 620 }}>
                    Добре дошъл! Готов ли си за следващата тренировка?
                  </Typography>
                </Box>

                <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1.25}>
                  {recommendedProgram ? (
                    <Button
                      variant="contained"
                      size="large"
                      startIcon={<PlayIcon />}
                      onClick={() => handleQuickStart(recommendedProgram.id)}
                    >
                      Стартирай {recommendedProgram.name}
                    </Button>
                  ) : (
                    <Button variant="contained" size="large" startIcon={<FitnessCenterIcon />} onClick={onViewPrograms}>
                      Виж програмите
                    </Button>
                  )}
                  <Button variant="outlined" size="large" startIcon={<EventNoteIcon />} onClick={onPlanWorkout}>
                    Планирай
                  </Button>
                  <Button variant="text" size="large" startIcon={<NotificationsIcon />} onClick={onOpenNotifications}>
                    Съвети от треньора
                  </Button>
                </Stack>

                <Grid container spacing={1.25}>
                  <Grid size={{ xs: 12, sm: 4 }}>
                    <Card elevation={0} sx={{ backgroundColor: alpha('#08111f', 0.28) }}>
                      <CardContent sx={{ p: 2, '&:last-child': { pb: 2 } }}>
                        <Typography variant="caption" color="text.secondary">
                          Активни програми
                        </Typography>
                        <Typography variant="h4">{activePrograms.length}</Typography>
                      </CardContent>
                    </Card>
                  </Grid>
                  <Grid size={{ xs: 12, sm: 4 }}>
                    <Card elevation={0} sx={{ backgroundColor: alpha('#08111f', 0.28) }}>
                      <CardContent sx={{ p: 2, '&:last-child': { pb: 2 } }}>
                        <Typography variant="caption" color="text.secondary">
                          Тренировки тази седмица
                        </Typography>
                        <Typography variant="h4">{totalCompletedWorkouts}</Typography>
                      </CardContent>
                    </Card>
                  </Grid>
                  <Grid size={{ xs: 12, sm: 4 }}>
                    <Card elevation={0} sx={{ backgroundColor: alpha('#08111f', 0.28) }}>
                      <CardContent sx={{ p: 2, '&:last-child': { pb: 2 } }}>
                        <Typography variant="caption" color="text.secondary">
                          Проследени минути
                        </Typography>
                        <Typography variant="h4">{totalTrackedMinutes}</Typography>
                      </CardContent>
                    </Card>
                  </Grid>
                </Grid>
              </Stack>
            </Grid>

            <Grid size={{ xs: 12, md: 5 }}>
              <Stack spacing={1.5} sx={{ height: '100%' }}>
                <ImagePlaceholder
                  title="Hero visual placeholder"
                  subtitle="Тук може да стои снимка, илюстрация или coach artwork."
                  minHeight={210}
                />
                <FocusedProgressWidget
                  items={focusedProgressItems}
                  loading={focusedProgressLoading}
                  error={focusedProgressError}
                  onViewPrograms={onViewPrograms}
                />
              </Stack>
            </Grid>
          </Grid>
        </CardContent>
      </Card>

      {error && (
        <Alert severity="error" onClose={() => setError(null)}>
          {error}
        </Alert>
      )}

      <Grid container spacing={{ xs: 2, md: 3 }}>
        <Grid size={{ xs: 12, xl: 8 }}>
          <Card elevation={0}>
            <CardContent sx={{ p: { xs: 2.25, md: 3 }, '&:last-child': { pb: { xs: 2.25, md: 3 } } }}>
              <Stack
                direction={{ xs: 'column', sm: 'row' }}
                spacing={1.5}
                justifyContent="space-between"
                alignItems={{ xs: 'flex-start', sm: 'center' }}
                sx={{ mb: 2.5 }}
              >
                <Box>
                  <Typography variant="h5">Активни програми</Typography>
                  <Typography variant="body2" color="text.secondary">
                    Бърз достъп до стартиране и проверка на тренировъчните дни.
                  </Typography>
                </Box>
                <Button variant="outlined" onClick={onViewPrograms}>
                  Виж всички
                </Button>
              </Stack>

              {activePrograms.length === 0 ? (
                <Alert severity="info">
                  Нямаш активни програми. Създай нова програма за да започнеш тренировка!
                  <Button variant="text" onClick={onViewPrograms} sx={{ ml: 1 }}>
                    Създай програма
                  </Button>
                </Alert>
              ) : (
                <Stack spacing={2}>
                  {activePrograms.map((program) => (
                    <Card
                      key={program.id}
                      elevation={0}
                      sx={{
                        backgroundColor: alpha('#0f172a', 0.72),
                        transition: 'transform 180ms ease, border-color 180ms ease',
                        '&:hover': {
                          transform: 'translateY(-2px)',
                          borderColor: alpha('#7dd3fc', 0.18),
                        },
                      }}
                    >
                      <CardContent sx={{ p: 2.25, '&:last-child': { pb: 2.25 } }}>
                        <Grid container spacing={2}>
                          <Grid size={{ xs: 12, md: 8 }}>
                            <Stack spacing={1.5}>
                              <Box>
                                <Typography variant="h6">{program.name}</Typography>
                                <Typography variant="body2" color="text.secondary">
                                  {program.description || 'Няма описание'}
                                </Typography>
                              </Box>
                              <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
                                <Chip icon={<CalendarIcon />} label={`${program.workoutsPerWeek} дни/седмица`} size="small" variant="outlined" />
                                <Chip icon={<TimeIcon />} label={`${program.durationWeeks} седмици`} size="small" variant="outlined" />
                                <Chip label={program.difficulty} size="small" color={program.difficulty === 'beginner' ? 'success' : program.difficulty === 'intermediate' ? 'warning' : 'error'} />
                              </Stack>
                              {programDays[program.id] && programDays[program.id].length > 1 && (
                                <Box>
                                  <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 1 }}>
                                    Избери ден
                                  </Typography>
                                  <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
                                    {programDays[program.id].map((day) => (
                                      <Button key={day} size="small" variant="outlined" onClick={() => onStartWorkout(program.id, day)}>
                                        Ден {day}
                                      </Button>
                                    ))}
                                  </Stack>
                                </Box>
                              )}
                            </Stack>
                          </Grid>
                          <Grid size={{ xs: 12, md: 4 }}>
                            <Stack spacing={1.5} sx={{ height: '100%', justifyContent: 'space-between' }}>
                              <ImagePlaceholder
                                title={`Program placeholder: ${program.name}`}
                                subtitle="Добави бъдеща снимка или cover за програмата."
                                minHeight={140}
                              />
                              <Button
                                variant="contained"
                                startIcon={<PlayIcon />}
                                onClick={() => handleQuickStart(program.id)}
                                fullWidth
                              >
                                Започни
                              </Button>
                            </Stack>
                          </Grid>
                        </Grid>
                      </CardContent>
                    </Card>
                  ))}
                </Stack>
              )}
            </CardContent>
          </Card>
        </Grid>

        <Grid size={{ xs: 12, xl: 4 }}>
          <Stack spacing={{ xs: 2, md: 3 }}>
            <Card elevation={0}>
              <CardContent sx={{ p: { xs: 2.25, md: 3 }, '&:last-child': { pb: { xs: 2.25, md: 3 } } }}>
                <Typography variant="h5" gutterBottom>
                  Последни тренировки
                </Typography>
                <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                  Последните логнати сесии, готови за бърз преглед или копиране.
                </Typography>
                <Divider sx={{ mb: 2 }} />

                {recentWorkouts.length === 0 ? (
                  <Typography variant="body2" color="text.secondary">
                    Все още нямаш логнати тренировки
                  </Typography>
                ) : (
                  <Stack spacing={1.5}>
                    {recentWorkouts.map((workout) => (
                      <Card key={workout.id} elevation={0} sx={{ backgroundColor: alpha('#0f172a', 0.72) }}>
                        <CardContent sx={{ p: 2, '&:last-child': { pb: 2 } }}>
                          <Stack spacing={1}>
                            <Stack direction="row" spacing={1} justifyContent="space-between" alignItems="flex-start">
                              <Box sx={{ minWidth: 0 }}>
                                <Typography variant="subtitle2">{workout.name}</Typography>
                                <Typography variant="caption" color="text.secondary">
                                  {formatDate(workout.workoutDate)}
                                </Typography>
                              </Box>
                              <Chip label={getStatusLabel(workout.status)} size="small" color={getStatusColor(workout.status)} />
                            </Stack>

                            <Stack direction="row" spacing={0.5} justifyContent="space-between" alignItems="center">
                              <Typography variant="caption" color="text.secondary">
                                {workout.durationMinutes ? `${workout.durationMinutes} мин` : 'Без отчетено време'}
                              </Typography>
                              <Stack direction="row" spacing={0.5}>
                                {trainerReportsByWorkoutId[workout.id] && (
                                  <IconButton
                                    size="small"
                                    color="primary"
                                    onClick={() => {
                                      void handleOpenReportDialog(workout.id);
                                    }}
                                    title="Пълен доклад от треньора"
                                  >
                                    <DescriptionIcon fontSize="small" />
                                  </IconButton>
                                )}
                                {workout.status === 'completed' && (
                                  <IconButton
                                    size="small"
                                    color={copiedWorkoutId === workout.id ? 'success' : 'default'}
                                    onClick={() => handleCopyWorkoutLog(workout.id)}
                                    title={copiedWorkoutId === workout.id ? 'Копирано!' : 'Копирай лог'}
                                    disabled={copyingWorkoutId === workout.id}
                                  >
                                    {copyingWorkoutId === workout.id ? <CircularProgress size={16} /> : <ContentCopyIcon fontSize="small" />}
                                  </IconButton>
                                )}
                                {workout.status === 'in_progress' && (
                                  <IconButton
                                    size="small"
                                    color="success"
                                    onClick={() => handleCompleteWorkout(workout.id)}
                                    title="Завърши тренировката"
                                  >
                                    <CheckCircleIcon fontSize="small" />
                                  </IconButton>
                                )}
                              </Stack>
                            </Stack>
                          </Stack>
                        </CardContent>
                      </Card>
                    ))}
                  </Stack>
                )}
              </CardContent>
            </Card>

            <Card elevation={0}>
              <CardContent sx={{ p: { xs: 2.25, md: 3 }, '&:last-child': { pb: { xs: 2.25, md: 3 } } }}>
                <Typography variant="h5" gutterBottom>
                  Статистика
                </Typography>
                <Divider sx={{ mb: 2 }} />
                <Stack spacing={1.5}>
                  <Box>
                    <Typography variant="caption" color="text.secondary">
                      Тренировки тази седмица
                    </Typography>
                    <Typography variant="h4" color="primary.main">
                      {totalCompletedWorkouts}
                    </Typography>
                  </Box>
                  <Box>
                    <Typography variant="caption" color="text.secondary">
                      Активни програми
                    </Typography>
                    <Typography variant="h4" color="secondary.main">
                      {activePrograms.length}
                    </Typography>
                  </Box>
                </Stack>
              </CardContent>
            </Card>

            <WeeklyMuscleChart data={weeklyMuscleSets} />

            <Card elevation={0}>
              <CardContent sx={{ p: { xs: 2.25, md: 3 }, '&:last-child': { pb: { xs: 2.25, md: 3 } } }}>
                <Typography variant="h5" gutterBottom>
                  Твоят AI треньор
                </Typography>
                <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                  Нотификациите и coach feedback остават достъпни от dashboard-а.
                </Typography>
                <Stack spacing={1.5}>
                  <ImagePlaceholder
                    title="Coach visual placeholder"
                    subtitle="Тук може да стои portrait или branded art за треньора."
                    minHeight={150}
                  />
                  <Card elevation={0} sx={{ backgroundColor: alpha('#08111f', 0.28) }}>
                    <CardContent sx={{ p: 2, '&:last-child': { pb: 2 } }}>
                      <Stack direction="row" spacing={1.25} alignItems="center">
                        <Box
                          sx={{
                            width: 40,
                            height: 40,
                            borderRadius: '14px',
                            display: 'grid',
                            placeItems: 'center',
                            bgcolor: alpha('#7dd3fc', 0.16),
                            color: 'primary.main',
                          }}
                        >
                          <SmartToyIcon fontSize="small" />
                        </Box>
                        <Box>
                          <Typography variant="subtitle2">Coach Nova</Typography>
                          <Typography variant="caption" color="text.secondary">
                            Анализира логовете ти и праща съвети
                          </Typography>
                        </Box>
                      </Stack>
                    </CardContent>
                  </Card>
                  <Button variant="contained" fullWidth startIcon={<NotificationsIcon />} onClick={onOpenNotifications}>
                    Виж съветите
                  </Button>
                </Stack>
              </CardContent>
            </Card>
          </Stack>
        </Grid>
      </Grid>

      <Card elevation={0}>
        <CardContent sx={{ p: { xs: 2.25, md: 3 }, '&:last-child': { pb: { xs: 2.25, md: 3 } } }}>
          <Typography variant="h5" gutterBottom>
            Бързи действия
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2.5 }}>
            Най-използваните действия са достъпни с големи tap targets и без пренатрупване.
          </Typography>
          <Grid container spacing={1.5}>
            <Grid size={{ xs: 12, sm: 6, lg: 3 }}>
              <Button variant="outlined" fullWidth startIcon={<FitnessCenterIcon />} onClick={onViewPrograms} sx={{ py: 1.8 }}>
                Виж програми
              </Button>
            </Grid>
            <Grid size={{ xs: 12, sm: 6, lg: 3 }}>
              <Button variant="outlined" fullWidth startIcon={<EventNoteIcon />} onClick={onPlanWorkout} sx={{ py: 1.8 }}>
                Планирай
              </Button>
            </Grid>
            <Grid size={{ xs: 12, sm: 6, lg: 3 }}>
              <Button variant="outlined" fullWidth startIcon={<TrendingUpIcon />} sx={{ py: 1.8 }} disabled>
                Прогрес
              </Button>
            </Grid>
            <Grid size={{ xs: 12, sm: 6, lg: 3 }}>
              <Button
                variant="outlined"
                fullWidth
                startIcon={exporting ? <CircularProgress size={20} /> : <DownloadIcon />}
                onClick={handleExportCsv}
                disabled={exporting}
                sx={{ py: 1.8 }}
              >
                {exporting ? 'Експортиране...' : 'Експорт CSV'}
              </Button>
            </Grid>
          </Grid>
        </CardContent>
      </Card>

      <Dialog
        open={reportDialogWorkoutId !== null}
        onClose={handleCloseReportDialog}
        fullWidth
        maxWidth="md"
      >
        <DialogTitle>
          Пълен доклад от треньора
          {selectedWorkout ? ` • ${selectedWorkout.name}` : ''}
        </DialogTitle>
        <DialogContent dividers>
          <Typography variant="subtitle2" sx={{ mb: 1 }}>
            Лог на тренировката
          </Typography>
          {reportDialogLogLoading ? (
            <CircularProgress size={18} />
          ) : (
            <Typography variant="body2" sx={{ whiteSpace: 'pre-wrap', fontFamily: 'monospace', mb: 2 }}>
              {reportDialogWorkoutLog || 'Няма наличен лог.'}
            </Typography>
          )}

          {selectedWorkoutReport?.recommendedAdaptations && selectedWorkoutReport.recommendedAdaptations.length > 0 && (
            <Box sx={{ mb: 2 }}>
              <Typography variant="subtitle2" sx={{ mb: 0.75 }}>
                Препоръчани адаптации за следващата тренировка
              </Typography>
              <Stack spacing={0.5}>
                {selectedWorkoutReport.recommendedAdaptations.map((adaptation, index) => (
                  <Typography key={`${adaptation}-${index}`} variant="body2">
                    {index + 1}. {adaptation}
                  </Typography>
                ))}
              </Stack>
            </Box>
          )}

          <Typography variant="subtitle2" sx={{ mb: 1 }}>
            Пълен доклад от треньора
          </Typography>
          <Typography variant="body2" sx={{ whiteSpace: 'pre-wrap' }}>
            {selectedWorkoutReport?.fullReport || 'Няма наличен доклад за тази тренировка.'}
          </Typography>
        </DialogContent>
      </Dialog>
    </Stack>
  );
}
